import React, { useContext, useState } from 'react';
import clsx from 'clsx';
import Avatar, { IAvatarProps } from '@srm/ui/avatar';
import UserAvatarPreviewModal, { type IUserAvatarPreview } from './UserAvatarPreviewModal';
import UserAvatarPreviewContext from './UserAvatarPreviewContext';
import { useAvatarSrc } from './avatarUrl';
import { DocumentRuntimeContext } from '@/context/DocumentRuntimeContext';

export interface UserAvatarProps extends Omit<IAvatarProps, 'src' | 'ref'> {
    /** Ключ элемента АК — нужен, чтобы загрузить реальное фото. */
    userKey?: string;
    /**
     * Есть ли у элемента фото. Если явно false — фото не запрашивается (показываются
     * инициалы), что исключает 404-шторм. undefined — фото пробуется (текущий пользователь).
     */
    hasImage?: boolean;
    /** Тип элемента ('user' | группы): группам — скруглённый квадрат вместо круга. */
    type?: string;
    /** Cache-bust версия (после смены своего аватара). */
    version?: number | string;
    /** Версия фото из DTO (photoVersion) — для версионированного ?v= URL (immutable-кэш). */
    photoVersion?: string;
    /** Клик по аватару открывает полноразмерное фото в модалке (?size=full). Внутри карточки пользователя окно
     *  открывает её владелец (`UserAvatarPreviewContext`): просмотр живёт после закрытия дропдауна. */
    zoomable?: boolean;
}

/**
 * Аватар пользователя/группы по ключу АК: вид — `Avatar` из `@srm/ui` (фото, иначе инициалы и цвет по имени),
 * здесь — загрузка фото (когда hasImage) и лайтбокс. forwardRef + проброс пропсов — чтобы компонент можно было
 * использовать как цель Tooltip/HoverCard. При zoomable клик открывает полноразмерное фото (?size=full) квадратом,
 * грузится лениво при открытии.
 */
const UserAvatar = React.forwardRef<HTMLDivElement, UserAvatarProps>(function UserAvatar(
    {
        userKey,
        name,
        hasImage,
        type,
        version,
        photoVersion,
        zoomable,
        size = 28,
        radius,
        className,
        onClick,
        style,
        ...others
    },
    ref,
) {
    const runtime = useContext(DocumentRuntimeContext);
    const openPreview = useContext(UserAvatarPreviewContext);
    const previewUserKey = runtime.mode === 'supportPreview' ? undefined : userKey;
    const isGroup = !!type && type !== 'user';
    const [opened, setOpened] = useState(false);
    const src = useAvatarSrc(
        previewUserKey,
        hasImage,
        photoVersion ?? version,
        Math.ceil(size * Math.min(window.devicePixelRatio || 1, 3)),
    );
    const canZoom = !!zoomable && !!src;
    const preview: IUserAvatarPreview = { userKey: previewUserKey, name, hasImage, version, photoVersion };

    const avatar = (
        <Avatar
            {...others}
            ref={ref}
            name={name}
            size={size}
            // Персоне радиус не задаём: круг Mantine (1000px) при любом размере; 'xl' темы — 20px, и от 40px
            // выходил скруглённый квадрат, который круглые обёртки обрезали вместе с кольцом.
            radius={radius ?? (isGroup ? 'sm' : undefined)}
            src={src}
            // rf-user-avatar — якорь правил Front поверх вида пакета (обводка в шапке hover-карточки)
            className={clsx('rf-user-avatar', className)}
            onClick={
                canZoom
                    ? (e) => {
                          // zoomable не должен «съедать» пользовательский onClick (напр. цель
                          // Tooltip/HoverCard): вызываем оба — сначала переданный обработчик, затем лайтбокс.
                          onClick?.(e);
                          if (openPreview) openPreview(preview);
                          else setOpened(true);
                      }
                    : onClick
            }
            style={[canZoom ? { cursor: 'zoom-in' } : undefined, style]}
        />
    );

    if (!zoomable) return avatar;

    return (
        <>
            {avatar}
            {opened && <UserAvatarPreviewModal {...preview} onClose={() => setOpened(false)} />}
        </>
    );
});

export default UserAvatar;
